// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/destroyer-sweep.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// destroyer-sweep.ts — DESTROYER's animated ground: ONE hairline bearing line
// turning around a set of concentric range rings in one corner of the shell.
//
// IT OWNS NO CANVAS, and that is the whole reason it is shaped this way.
// PR-4's style-axis.ts owns the element, the ONE-canvas cap, the rAF loop and
// pause-on-hidden; this module registers a PAINTER (`(ctx, t) => void`) and is
// called once per frame with the substrate's clock. A style that rolled its
// own loop would sit outside every arm scripts/perf-budget.mjs can drive.
//
// WHAT IT DRAWS, AND WHAT IT DELIBERATELY DOES NOT (spec §4).
// The instrument is mostly CSS: the etched deck coating, the five range rings
// and the 360°-scale ticks are static `--dz-plate` background layers on the
// z-0 ground, so they are painted once by the compositor and cost nothing per
// frame. The canvas carries only the parts that MOVE:
//   * one bearing line at `--dz-sweep-alpha` (24%), one revolution per 24 s,
//     LINEAR, with a short AFTERGLOW wedge behind it (`--dz-sweep-trail`,
//     fading to 0 over TRAIL_DEG) — the radar's phosphor trail. The trail is
//     a FIELD a glyph can sit on, and the gate bounds it hard: Haze Gray's
//     bare-ground accent holds its 3.0 floor only up to a .05 composite
//     field, which with the plate's 4% inside it leaves the trail at .01
//     (destroyer.css names the measurements). The LINE is the visible radar.
//   * the PING: as the line crosses a ring, a short arc of that ring
//     brightens by PING_ALPHA and decays back over PING_MS. A ping, not a
//     strobe — and it is decoration with a fixed script, NOT a visualisation.
//     There are no blips and no data binding.
//
// OWNER RULING (2026-09-08): "The destroyer background should be animated
// with a radar sweep." MEASURED on the build before this change, Helm and
// Bridge home, dark and light: the painter WAS registered and WAS ticking
// (30 frames / 630 ctx calls per 500 ms) and the line WAS moving (~3,600
// canvas px changed per second) — at a peak painted alpha of 0.059, which
// composited to a line:ground contrast of 1.10-1.12:1 (a 10-11/255 channel
// delta). Invisible, by the numbers, on every theme. R2.3's 6% ambient cap
// and 60 s floor produced exactly the acceptance criterion destroyer-1 wrote
// ("if a user can consciously see it moving, the opacity is wrong"), and the
// owner has now ruled the other way for THIS instrument: it must be seen
// sweeping. The ceilings below are the new contract; scripts/style-contrast
// composites the FIELD (plate + trail) under bare-ground text and the LINE
// (plate + line) under every material tier, and the values shipped are the
// largest that clear every floor — see destroyer.css `--dz-sweep-*`.
//
// HOW BIG THE SWEEP IS, AND WHY IT IS NOT A CONSTANT (owner ruling
// 2026-09-09): "make the radar sweep sized to match the background so that
// the radar sweep sweeps the whole area of the background instead of just the
// smaller inner part of the circle." The static plate is built from
// `repeating-radial-gradient`, which is UNBOUNDED — the range rings march
// outward across the whole viewport, however large it is. The sweep was a
// hardcoded `SWEEP_R = 340`, so at every real viewport the rings extended
// hundreds of px past the line that was supposed to be sweeping them.
// MEASURED with that constant re-planted on the shipped painter
// (e2e/style-probe.mjs's `fixed-radius` control), six viewport x bearing
// cells: the line reached 341px every time, against a field boundary of
// 569-1175px along its own bearing — 29% to 60% of the background it was
// supposed to be sweeping. The same six cells read 99-100% after.
//
// The radius is now the field's CIRCUMRADIUS about the origin: the largest
// distance from the origin to any point of the element. The element is the
// rectangle [0,w] x [0,h]; `‖x − p‖` is convex, and a convex function over a
// compact convex set attains its maximum at an EXTREME POINT, whose set for a
// rectangle is exactly its four corners. So `max` over the four corners is
// not an approximation and carries no aspect-ratio assumption — it is the
// exact answer at every shape, and it stays exact if ORIGIN_X / ORIGIN_Y ever
// move (which is why it is a max over four rather than "the top-left one",
// the corner that happens to win while the origin sits at 72% / 102%).
//
// The COST that buys: `clearRect` is now the whole canvas at every real
// viewport, where before it was the clipped corner disc. The budget is
// reported by e2e/style-probe.mjs's frame line, measured on both sides of
// this change — see the PR. It is still ONE clear + one conic fill + one
// hairline stroke + N short arcs per frame; nothing per-pixel, nothing
// compounding, and the whole thing is still skipped outright on every reduce
// lane (the element is never constructed).
//
// WHY THERE IS NO IntersectionObserver. The spec asks for one so the sweep
// stops when the corner scrolls out of view. `#style-bg` is `position: fixed;
// inset: 0`, so the corner region is in the viewport whenever the tab is
// visible — the observer would fire exactly when `visibilitychange` already
// does, and binding a listener HERE is the one thing that would put this
// module outside the substrate's pause-on-hidden proof. The substrate's guard
// is the same guarantee, measured by a gate that drives it.
//
// WHY IT NEVER RUNS UNDER A REDUCE LANE: style-axis.ts's effectiveGrade()
// degrades `canvas` to `gradient` whenever prefers-reduced-motion OR
// prefers-reduced-transparency matches, so the element is never constructed
// and this painter is never called. styles.css then paints the SAME
// instrument with the bearing line frozen at 000 — same asset, zero motion,
// no layout shift.

/** The hard alpha ceiling for the bearing line, mirrored here so a stylesheet
 * that loses the token cannot silently raise it. `--dz-sweep-alpha` is the
 * live value; this is what it is clamped to. Owner ruling 2026-09-08: the
 * sweep must be VISIBLE; the gate's ground arm is what bounds it. */
export const SWEEP_ALPHA_CEILING = 0.30;

/** The afterglow's peak alpha ceiling (`--dz-sweep-trail` is the live value).
 * The trail is a FIELD a glyph can sit on, so it is the smaller number and the
 * one the gate composites under bare-ground text. */
export const TRAIL_ALPHA_CEILING = 0.10;

/** How far behind the line the afterglow reaches, degrees of bearing. */
export const TRAIL_DEG = 48;

/** One revolution, ms. Owner ruling 2026-09-08: a radar sweep the viewer can
 * see turning. 24 s is 15 deg/s — unmistakably a sweep, never a strobe; the
 * smoke holds it inside [SWEEP_PERIOD_MIN_MS, SWEEP_PERIOD_MAX_MS]. */
export const SWEEP_PERIOD_MS = 24_000;
export const SWEEP_PERIOD_MIN_MS = 12_000;
export const SWEEP_PERIOD_MAX_MS = 60_000;

/** The ping: how much a crossed ring brightens, and how long it takes to
 * decay back. Raised with the line (2026-09-08) so the ring lights as the
 * sweep crosses it; still a transient on one arc, never a field. */
const PING_ALPHA = 0.06;
const PING_MS = 2000;

/** The corner the instrument is anchored to, as a fraction of the viewport.
 * Off-canvas on BOTH axes so only the upper-left quadrant of the rose is ever
 * on screen — a plan-position indicator seen from the corner of the room. */
const ORIGIN_X = 0.72;
const ORIGIN_Y = 1.02;

/** The range-ring PITCH, CSS px — the fallback only. The live value is read
 * from `--dz-ring-step`, and destroyer.css builds the static plate's
 * `repeating-radial-gradient` out of that SAME token, so there is exactly one
 * declaration of "63px" in the product and the pings cannot drift off the
 * painted rings. This constant is what a stylesheet that has LOST the token
 * degrades to, not a second source. */
const RING_STEP_FALLBACK = 63;

/** A bound on how many ping arcs one frame may stroke. The rings are derived
 * from the radius, so an 8K display would otherwise ask for 112 of them and
 * the arc length summed over the set grows with n^2. 48 rings is 3024px of
 * ping reach — past any display this ships on — and the line and the trail
 * are never capped, so the SWEEP still covers the field at every size; only
 * the decorative echo stops being drawn beyond it. */
const MAX_RINGS = 48;

/** The field's circumradius about the origin: the distance to the FARTHEST of
 * the four corners. Exact at every aspect ratio (see the header). */
export function sweepRadius(w: number, h: number): number {
  const cx = ORIGIN_X * w;
  const cy = ORIGIN_Y * h;
  let r = 0;
  for (const corner of [[0, 0], [w, 0], [0, h], [w, h]] as const) {
    const d = Math.hypot((corner[0] ?? 0) - cx, (corner[1] ?? 0) - cy);
    if (d > r) r = d;
  }
  return r;
}

/** The ring radii the PING lights: the plate's own pitch, repeated out to the
 * sweep's radius (capped at MAX_RINGS). The plate's gradient is unbounded, so
 * this is the same ladder the compositor already painted. */
export function sweepRings(radius: number, step: number): number[] {
  const out: number[] = [];
  if (!(step > 0) || !(radius > 0)) return out;
  for (let r = step; r <= radius && out.length < MAX_RINGS; r += step) out.push(r);
  return out;
}

/** How often the painter re-reads layout and theme tokens (ms). Both are
 * getComputedStyle / clientWidth reads — cheap, but not 60 times a second.
 * A resize or a theme switch lands within one of these windows. */
const REFRESH_MS = 500;

/** dpr is capped: a 3x backing store for four hairlines is pure cost. */
const MAX_DPR = 2;

interface Env {
  at: number;
  w: number;
  h: number;
  dpr: number;
  alpha: number;
  trail: number;
  ink: string;
  /** Derived from w/h every refresh window — a resize or a theme switch lands
   * within one of them, which is why this module still binds no listener. */
  radius: number;
  rings: number[];
}

let env: Env | null = null;
/** Painter-clock timestamp at which each ring was last crossed. `-1` = not
 * yet crossed since boot, so the first revolution lights them in order
 * instead of flashing every ring on frame one. Resized with the ring set
 * whenever the field's geometry changes. */
let lastCrossed: number[] = [];
let lastTheta = -1;

function readEnv(canvas: HTMLCanvasElement, now: number): Env {
  const cs = getComputedStyle(document.documentElement);
  const tok = (name: string, fallback: string): string => cs.getPropertyValue(name).trim() || fallback;
  const declared = parseFloat(tok('--dz-sweep-alpha', String(SWEEP_ALPHA_CEILING)));
  const alpha = Number.isFinite(declared)
    ? Math.min(Math.max(declared, 0), SWEEP_ALPHA_CEILING)
    : SWEEP_ALPHA_CEILING;
  const declaredTrail = parseFloat(tok('--dz-sweep-trail', '0'));
  const trail = Number.isFinite(declaredTrail)
    ? Math.min(Math.max(declaredTrail, 0), TRAIL_ALPHA_CEILING)
    : 0;
  const w = canvas.clientWidth || 1;
  const h = canvas.clientHeight || 1;
  const dpr = Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, MAX_DPR);
  const want = { w: Math.round(w * dpr), h: Math.round(h * dpr) };
  if (canvas.width !== want.w || canvas.height !== want.h) {
    canvas.width = want.w;
    canvas.height = want.h;
  }
  // The plate's own pitch, from the plate's own token. A stylesheet without it
  // (or with a non-length) degrades to the fallback rather than to no rings.
  const declaredStep = parseFloat(tok('--dz-ring-step', String(RING_STEP_FALLBACK)));
  const step = Number.isFinite(declaredStep) && declaredStep > 0 ? declaredStep : RING_STEP_FALLBACK;
  const radius = sweepRadius(w, h);
  const rings = sweepRings(radius, step);
  if (lastCrossed.length !== rings.length) {
    const next = rings.map((_, i) => lastCrossed[i] ?? -1);
    lastCrossed = next;
  }
  return { at: now, w, h, dpr, alpha, trail, ink: tok('--ink', '#e6e9ec'), radius, rings };
}

/** `#rrggbb` (or any resolved colour) + alpha -> an `rgba()` the canvas can
 * take. A non-hex value is handed back unchanged rather than guessed at. */
export function withAlpha(color: string, a: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  const h = m?.[1];
  if (!h) return color;
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * THE painter. Registered against the DESTROYER style id; the substrate calls
 * it once per animation frame with a monotonic clock.
 */
export function paintDestroyerSweep(ctx: CanvasRenderingContext2D, t: number): void {
  const canvas = ctx.canvas as HTMLCanvasElement;
  if (!env || t - env.at > REFRESH_MS || t < env.at) env = readEnv(canvas, t);
  const { w, h, dpr, alpha, trail, ink, radius, rings } = env;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const cx = ORIGIN_X * w;
  const cy = ORIGIN_Y * h;
  // Clear the SWEPT REGION, clipped to the element. `radius` is the field's
  // circumradius, so at a real viewport this is the whole canvas by
  // construction — that is the owner's ruling, not an oversight, and the
  // clamping stays because it is what keeps the call bounded by the geometry
  // rather than being a blind full-canvas clear that would survive any future
  // change to the radius.
  const x0 = Math.max(0, cx - radius);
  const y0 = Math.max(0, cy - radius);
  ctx.clearRect(x0, y0, Math.min(w, cx + radius) - x0, Math.min(h, cy + radius) - y0);

  // Bearing, measured the way a PPI does: 0 rad points UP (000 true), turning
  // clockwise. Canvas angles start at +x, hence the -PI/2.
  const turns = t / SWEEP_PERIOD_MS;
  const theta = (turns - Math.floor(turns)) * Math.PI * 2 - Math.PI / 2;

  // ---- the ping ---------------------------------------------------------
  // A ring is "crossed" on the frame the bearing passes its own reference
  // angle. They all share one origin, so they are crossed together — the
  // stagger comes from each ring's own decay, which is why the arc is drawn
  // at a per-ring alpha rather than the ring being redrawn.
  if (lastTheta > theta) for (let i = 0; i < rings.length; i++) lastCrossed[i] = t;
  else if (lastTheta < 0) for (let i = 0; i < rings.length; i++) lastCrossed[i] = t;
  lastTheta = theta;

  ctx.lineWidth = 1;
  for (let i = 0; i < rings.length; i++) {
    const since = lastCrossed[i] ?? -1;
    if (since < 0) continue;
    const k = (t - since) / PING_MS;
    if (k >= 1) continue;
    const r = rings[i] ?? 0;
    ctx.strokeStyle = withAlpha(ink, PING_ALPHA * (1 - k));
    ctx.beginPath();
    ctx.arc(cx, cy, r, theta - 0.38, theta);
    ctx.stroke();
  }

  // ---- the afterglow ----------------------------------------------------
  // A sector behind the line, transparent at its trailing edge and `trail`
  // at the line: the phosphor decay of a PPI. One conic gradient + one fill
  // per frame where the browser has createConicGradient; a stepped fan of
  // SLICES otherwise (same geometry, same peak, no gradient object).
  if (trail > 0) {
    const span = (TRAIL_DEG * Math.PI) / 180;
    const from = theta - span;
    const conic = (ctx as CanvasRenderingContext2D & { createConicGradient?: (a: number, x: number, y: number) => CanvasGradient })
      .createConicGradient;
    if (typeof conic === 'function') {
      const g = conic.call(ctx, from, cx, cy);
      const k = span / (Math.PI * 2);
      g.addColorStop(0, withAlpha(ink, 0));
      g.addColorStop(k, withAlpha(ink, trail));
      g.addColorStop(Math.min(1, k + 0.0005), withAlpha(ink, 0));
      g.addColorStop(1, withAlpha(ink, 0));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, from, theta);
      ctx.closePath();
      ctx.fill();
    } else {
      const slices = 8;
      for (let i = 0; i < slices; i++) {
        const a0 = from + (span * i) / slices;
        const a1 = from + (span * (i + 1)) / slices;
        ctx.fillStyle = withAlpha(ink, (trail * (i + 0.5)) / slices);
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, radius, a0, a1);
        ctx.closePath();
        ctx.fill();
      }
    }
  }

  // ---- the bearing line -------------------------------------------------
  ctx.lineWidth = 1;
  ctx.strokeStyle = withAlpha(ink, alpha);
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + radius * Math.cos(theta), cy + radius * Math.sin(theta));
  ctx.stroke();
}

/** Test seam — the gate and the smoke drive the painter across arms. */
export function resetDestroyerSweep(): void {
  env = null;
  lastTheta = -1;
  lastCrossed = [];
}

/** The instrument's geometry AT A GIVEN FIELD SIZE, for the gate and the
 * smoke. It takes the element's size because the radius and the ring set are
 * now derived from it — a describe() that answered with a constant is exactly
 * what let the sweep and the plate disagree for a day. `step` defaults to the
 * fallback pitch; pass the live `--dz-ring-step` to describe what is painted. */
export function sweepRegion(
  w: number,
  h: number,
  step: number = RING_STEP_FALLBACK,
): { originX: number; originY: number; radius: number; rings: number[]; trailDeg: number } {
  const radius = sweepRadius(w, h);
  return { originX: ORIGIN_X, originY: ORIGIN_Y, radius, rings: sweepRings(radius, step), trailDeg: TRAIL_DEG };
}

/** The bearing the painter draws at clock `t`, degrees clockwise from 000
 * (up). Exported so a probe can predict the angle it should measure on the
 * canvas rather than trusting a screenshot. */
export function sweepBearingDeg(t: number): number {
  const turns = t / SWEEP_PERIOD_MS;
  return (turns - Math.floor(turns)) * 360;
}
